import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store/useStore';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { getDayList } from '@mui/x-scheduler-headless/get-day-list';
import { useEventTimelinePremiumStoreContext } from '@mui/x-scheduler-headless-premium/use-event-timeline-premium-store-context';
import { eventTimelinePremiumViewSelectors } from '@mui/x-scheduler-headless-premium/event-timeline-premium-selectors';
import { formatWeekDayMonthAndDayOfMonth, useFormatTime } from '@mui/x-scheduler/internals';
import { useEventTimelinePremiumStyledContext } from "../../EventTimelinePremiumStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TimeHeaderRoot = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'TimeHeader'
})({
  display: 'flex',
  minWidth: 'calc(var(--unit-count) * var(--time-cell-width))'
});
const TimeHeaderCell = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'TimeHeaderCell'
})(({
  theme
}) => ({
  flexGrow: 1,
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  '&:not(:last-child)': {
    borderRight: `1px solid ${(theme.vars || theme).palette.divider}`
  }
}));
const DayLabel = styled('time', {
  name: 'MuiEventTimeline',
  slot: 'TimeHeaderDayLabel'
})(({
  theme
}) => ({
  padding: theme.spacing(1),
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  display: 'flex',
  justifyContent: 'center',
  borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`
}));
const TimeCellsRow = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'TimeHeaderCellsRow'
})(({
  theme
}) => ({
  padding: theme.spacing(0, 1),
  display: 'grid',
  gridTemplateColumns: 'repeat(24, var(--time-cell-width))'
}));
const TimeCell = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'TimeHeaderTimeCell'
})(({
  theme
}) => ({
  padding: theme.spacing(1, 0),
  textAlign: 'center'
}));
const TimeLabel = styled('time', {
  name: 'MuiEventTimeline',
  slot: 'TimeHeaderTimeLabel'
})(({
  theme
}) => ({
  fontSize: theme.typography.body2.fontSize,
  color: (theme.vars || theme).palette.text.secondary
}));
export function TimeHeader(props) {
  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventTimelinePremiumStoreContext();
  const {
    classes
  } = useEventTimelinePremiumStyledContext();

  // Selector hooks
  const viewConfig = useStore(store, eventTimelinePremiumViewSelectors.config);

  // Feature hooks
  const formatTime = useFormatTime();
  const days = React.useMemo(() => getDayList({
    adapter,
    start: viewConfig.start,
    end: viewConfig.end
  }), [adapter, viewConfig]);
  const template = adapter.date('2020-01-01T00:00:00', 'default');
  return /*#__PURE__*/_jsx(TimeHeaderRoot, _extends({
    className: classes.timeHeader
  }, props, {
    children: days.map(day => /*#__PURE__*/_jsxs(TimeHeaderCell, {
      className: classes.timeHeaderCell,
      children: [/*#__PURE__*/_jsx(DayLabel, {
        dateTime: day.key,
        className: classes.timeHeaderDayLabel,
        children: formatWeekDayMonthAndDayOfMonth(day.value, adapter)
      }), /*#__PURE__*/_jsx(TimeCellsRow, {
        className: classes.timeHeaderCellsRow,
        children: Array.from({
          length: 24
        }, (_, hour) => /*#__PURE__*/_jsx(TimeCell, {
          className: classes.timeHeaderTimeCell,
          style: {
            '--hour': hour
          },
          children: /*#__PURE__*/_jsx(TimeLabel, {
            className: classes.timeHeaderTimeLabel,
            children: formatTime(adapter.setHours(template, hour))
          })
        }, hour))
      })]
    }, day.key))
  }));
}